import { useParams, useNavigate } from "react-router-dom";
import { getArticleById } from "@/data/articles";
import ReadingProgress from "@/components/ReadingProgress";
import ArticleHeader from "@/components/ArticleHeader";
import ArticleHero from "@/components/ArticleHero";
import AbstractTabs from "@/components/AbstractTabs";
import KeywordBadges from "@/components/KeywordBadges";
import AccordionSection from "@/components/AccordionSection";
import ReferenceList from "@/components/ReferenceList";
import { ArrowLeft, Quote } from "lucide-react";
import InteractivePanel from "@/components/InteractivePanel";

const NAV_SECTIONS = [
  { id: "resumen", title: "Resumen" },
  { id: "introduccion", title: "Introducción" },
  { id: "metodologia", title: "Metodología" },
  { id: "resultados", title: "Resultados" },
  { id: "conclusiones", title: "Conclusiones" },
  { id: "referencias", title: "Referencias" },
];

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const article = getArticleById(Number(id));

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold text-[var(--riee-purple)] mb-2">Artículo no encontrado</p>
          <button
            className="btn-download mt-4"
            onClick={() => navigate("/")}
          >
            <ArrowLeft size={14} />
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  // APA citation string
  const authorsCitation = article.authors
    .map((a) => {
      const parts = a.name.split(" ");
      const last = parts[parts.length - 1];
      const initials = parts
        .slice(0, -1)
        .map((p) => p[0] + ".")
        .join(" ");
      return `${last}, ${initials}`;
    })
    .join(", ");

  const apaCitation = `${authorsCitation} (2026). ${article.title}. Revista Internacional de Estudios en Educación, 26(1), ${article.pagesStart}-${article.pagesEnd}. https://doi.org/${article.doi}`;

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Reading progress */}
      <ReadingProgress />

      {/* Sticky header */}
      <ArticleHeader articleId={article.id} sections={NAV_SECTIONS} />

      {/* Hero */}
      <ArticleHero article={article} />

      {/* Article body */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-14">

        {/* Abstract */}
        <section id="resumen" className="section-anchor">
          <h2
            className="text-2xl font-bold text-[var(--riee-purple)] mb-6 pb-2 border-b border-[var(--border)]"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            Resumen / Abstract
          </h2>
          <AbstractTabs es={article.abstract.es} en={article.abstract.en} />

          {/* Keywords */}
          <div className="mt-8">
            <h3
              className="text-base font-semibold text-gray-700 mb-4"
              style={{ fontFamily: "var(--font-serif)" }}
            >
              Palabras clave / Keywords
            </h3>
            <KeywordBadges keywords={article.keywords} />
          </div>
        </section>

        {/* Divider */}
        <div className="riee-divider">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <rect x="3.5" y="0" width="3" height="10" fill="var(--riee-gold)" opacity="0.5"/>
            <rect x="0" y="3.5" width="10" height="3" fill="var(--riee-gold)" opacity="0.5"/>
          </svg>
        </div>

        {/* Introduction */}
        <section id="introduccion" className="section-anchor">
          <h2
            className="text-2xl font-bold text-[var(--riee-purple)] mb-6 pb-2 border-b border-[var(--border)]"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            Introducción
          </h2>
          <div className="space-y-4">
            {article.introduction.map((p, i) => (
              <p key={i} className="text-gray-700 leading-relaxed" style={{ fontFamily: "var(--font-serif)", fontSize: "1.05rem" }}>
                {p}
              </p>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className="riee-divider">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <rect x="3.5" y="0" width="3" height="10" fill="var(--riee-gold)" opacity="0.5"/>
            <rect x="0" y="3.5" width="10" height="3" fill="var(--riee-gold)" opacity="0.5"/>
          </svg>
        </div>

        {/* Methodology */}
        <section id="metodologia" className="section-anchor">
          <h2
            className="text-2xl font-bold text-[var(--riee-purple)] mb-6 pb-2 border-b border-[var(--border)]"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            Metodología
          </h2>
          <AccordionSection sections={article.methodology} title="" />
        </section>

        {/* Divider */}
        <div className="riee-divider">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <rect x="3.5" y="0" width="3" height="10" fill="var(--riee-gold)" opacity="0.5"/>
            <rect x="0" y="3.5" width="10" height="3" fill="var(--riee-gold)" opacity="0.5"/>
          </svg>
        </div>

        {/* Results */}
        <section id="resultados" className="section-anchor">
          <h2
            className="text-2xl font-bold text-[var(--riee-purple)] mb-6 pb-2 border-b border-[var(--border)]"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            Resultados y hallazgos
          </h2>

          {/* Narrative results cards */}
          <div className="grid gap-5 sm:grid-cols-2 mb-10">
            {article.results.map((sec) => (
              <div
                key={sec.id}
                className="card-lift bg-white border border-[var(--border)] rounded-xl p-5"
              >
                <h3
                  className="font-semibold text-[var(--riee-purple)] mb-3 text-base"
                  style={{ fontFamily: "var(--font-serif)" }}
                >
                  {sec.title}
                </h3>
                {sec.content.map((p, i) => (
                  <p key={i} className="text-sm text-gray-700 leading-relaxed mb-2 last:mb-0">
                    {p}
                  </p>
                ))}
              </div>
            ))}
          </div>

          {/* ── Interactive visualizations ── */}
          <div className="mt-2">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                style={{ background: "var(--riee-gold)", color: "#1a1a2e" }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <rect x="1" y="6" width="2" height="5" rx="0.5" fill="currentColor"/>
                  <rect x="5" y="3" width="2" height="8" rx="0.5" fill="currentColor"/>
                  <rect x="9" y="1" width="2" height="10" rx="0.5" fill="currentColor"/>
                </svg>
                Visualizaciones interactivas
              </span>
              <div className="flex-1 h-px bg-[var(--riee-gold)] opacity-20" />
            </div>
            <InteractivePanel articleId={article.id} />
          </div>
        </section>

        {/* Divider */}
        <div className="riee-divider">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <rect x="3.5" y="0" width="3" height="10" fill="var(--riee-gold)" opacity="0.5"/>
            <rect x="0" y="3.5" width="10" height="3" fill="var(--riee-gold)" opacity="0.5"/>
          </svg>
        </div>

        {/* Conclusions */}
        <section id="conclusiones" className="section-anchor">
          <h2
            className="text-2xl font-bold text-[var(--riee-purple)] mb-6 pb-2 border-b border-[var(--border)]"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            Conclusiones
          </h2>
          <div className="space-y-4">
            {article.conclusions.map((c, i) => (
              <div key={i} className="flex gap-3">
                <span
                  className="flex-shrink-0 w-7 h-7 rounded-full bg-[var(--riee-purple)] text-white text-xs font-bold flex items-center justify-center mt-0.5"
                >
                  {i + 1}
                </span>
                <p className="text-gray-700 leading-relaxed" style={{ fontFamily: "var(--font-serif)", fontSize: "1.05rem" }}>
                  {c}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className="riee-divider">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <rect x="3.5" y="0" width="3" height="10" fill="var(--riee-gold)" opacity="0.5"/>
            <rect x="0" y="3.5" width="10" height="3" fill="var(--riee-gold)" opacity="0.5"/>
          </svg>
        </div>

        {/* References */}
        <section id="referencias" className="section-anchor">
          <h2
            className="text-2xl font-bold text-[var(--riee-purple)] mb-6 pb-2 border-b border-[var(--border)]"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            Referencias
          </h2>
          <ReferenceList references={article.references} />
        </section>

        {/* Citation box */}
        <section className="bg-[var(--secondary)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Quote size={16} className="text-[var(--riee-gold)]" />
            <h3 className="text-sm font-bold text-[var(--riee-purple)] uppercase tracking-wider">
              Cita sugerida (APA)
            </h3>
          </div>
          <p
            className="text-sm text-gray-700 leading-relaxed"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            {apaCitation}
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-[var(--border)] py-8 text-center mt-12">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm text-[var(--riee-purple)] hover:text-[var(--riee-gold-dark)] font-medium transition-colors"
        >
          <ArrowLeft size={14} />
          Volver a todos los artículos
        </button>
        <p className="text-xs text-gray-400 mt-3">
          RIEE Vol. 26, Núm. 1 (2026) · Universidad de Montemorelos
        </p>
      </footer>
    </div>
  );
}
